
import { defineComponent, toRefs, computed, PropType } from 'vue';
import useNumbers from '@/composables/useNumbers';
import useTokenApproval from '@/composables/trade/useTokenApproval';
import useRelayerApproval, {
  Relayer
} from '@/composables/trade/useRelayerApproval';
import useTokens from '@/composables/useTokens';
import { FullPool } from '@/services/balancer/subgraph/types';


import FarmActionsCard from '@/embr/components/pages/farm/FarmActionsCard.vue';
import NormalPoolActionsCard from '@/embr/components/pages/farm/NormalPoolActionsCard.vue';
import { NATIVE_ASSET_ADDRESS } from '@/constants/tokens';
import { getWrapAction, WrapType } from '@/lib/utils/balancer/wrapper';
import { isStETH } from '@/lib/utils/balancer/lido';
import { getAddress } from '@ethersproject/address';
import { bnum } from '@/lib/utils';
import useUserSettings from '@/composables/useUserSettings';

export default defineComponent({
  emits: ['trade', 'close'],
  props: {
    open: {
      type: Boolean,
      default: false
    },
    isV1Swap: {
      type: Boolean,
      required: true
    },
    addressIn: {
      type: String,
      required: true
    },
    amountIn: {
      type: String,
      required: true
    },
    addressOut: {
      type: String,
      required: true
    },
    amountOut: {
      type: String,
      required: true
    },
    trading: {
      type: Boolean,
      required: true
    },
    pool: {
      type: Object as PropType<FullPool>
    }
  },
  components: {
    FarmActionsCard,
    NormalPoolActionsCard
  },
  setup(props: any, { emit }) {
    const { fNum, toFiat } = useNumbers();
    const { addressIn, amountIn, addressOut, isV1Swap } = toRefs(props);

    const { balanceFor, tokens, approvalRequired, wrappedNativeAsset, nativeAsset } = useTokens();
    const { currency } = useUserSettings();

    const wrapType = computed(() =>
      getWrapAction(addressIn.value, addressOut.value)
    );
    const isWrap = computed(() => wrapType.value === WrapType.Wrap);
    const isUnwrap = computed(() => wrapType.value === WrapType.Unwrap);

    const isStETHTrade = computed(
      () =>
        isStETH(addressIn.value, addressOut.value) &&
        wrapType.value === WrapType.NonWrap
    );

    const tokenApproval = useTokenApproval(addressIn, amountIn, tokens);

    const lidoRelayerApproval = useRelayerApproval(Relayer.LIDO, isStETHTrade);

    const valueIn = computed(() => toFiat(amountIn.value, addressIn.value));

    const symbolIn = computed(() => {
      const token = tokens.value[addressIn.value];
      if (!token) {
        return '';
      }
      return token.symbol;
    });

    const symbolOut = computed(() => {
      const token = tokens.value[addressOut.value];
      if (!token) {
        return '';
      }
      return token.symbol;
    });

    const isEthTrade = computed(() => addressIn.value === NATIVE_ASSET_ADDRESS);

    const requiresTokenApproval = computed(() =>
      isUnwrap.value || isEthTrade.value ? false : true
    );

    const isLidoRelayerApproved = computed(
      () => lidoRelayerApproval.isUnlocked.value
    );
    const requiresLidoRelayerApproval = computed(
      () =>
        isStETHTrade.value &&
        (!isLidoRelayerApproved.value || lidoRelayerApproval.approved.value)
    );

    const requiresApproval = computed(
      () => requiresTokenApproval.value || requiresLidoRelayerApproval.value
    );

    const isTokenApproved = computed(() => {
      if (tokenApproval.approved.value) {
        return true;
      }

      const { isUnlockedV1, isUnlockedV2 } = tokenApproval.allowanceState.value;
      if (isWrap.value && !isEthTrade.value) {
        // If we're wrapping a token other than native ETH
        // we need to approve the underlying on the wrapper
        return !approvalRequired(
          addressIn.value,
          amountIn.value,
          addressOut.value
        );
      }
      return isV1Swap.value ? isUnlockedV1 : isUnlockedV2;
    });

    async function approveLidoRelayer(): Promise<void> {
      await lidoRelayerApproval.approve();
    }

    async function approveToken(): Promise<void> {
      if (isWrap.value && !isEthTrade.value) {
        // If we're wrapping a token other than native ETH
        // we need to approve the underlying on the wrapper
        await tokenApproval.approveSpender(addressOut.value);
      } else if (isV1Swap.value) {
        await tokenApproval.approveV1();
      } else {
        await tokenApproval.approveV2();
      }
    }

    const approvingToken = computed(() => tokenApproval.approving.value);

    const approvingLidoRelayer = computed(
      () => lidoRelayerApproval.approving.value
    );

    const totalRequiredTransactions = computed(() => {
      let txCount = 1; // trade

      if (requiresTokenApproval.value) {
        txCount++;
      }
      if (requiresLidoRelayerApproval.value) {
        txCount++;
      }
      return txCount;
    });
    
    const fiatTotal = computed(() => {
      const fiatValue = props.pool.tokenAddresses
        .map(address => {
          let tokenBalance = '0';

          if (address === wrappedNativeAsset.value.address) {
            const wrappedBalance = balanceFor(address);
            const nativeBalance = balanceFor(nativeAsset.address);
            tokenBalance = bnum(nativeBalance).gt(wrappedBalance)
              ? nativeBalance
              : wrappedBalance;
          } else {
            tokenBalance = balanceFor(address);
          }

          return toFiat(tokenBalance, address);
        })
        .reduce((total, value) =>
          bnum(total)
            .plus(value)
            .toString()
        );

      return fNum(fiatValue, currency.value);
    });

    const hasUnstakedBpt = computed(
      () =>
        props.pool?.farm &&
        parseFloat(balanceFor(getAddress(props.pool?.address))) > 0
    );

    const hasFarm = computed(
      () =>
        !!props.pool?.farm ||
        props.pool?.address == '0xae0464DE7a71CF733659C93CEE11F35A5Ad24004' ||
        props.pool?.address == '0x9042D74bDf78fdB27005C8065F49Cfc20ec4296c'
    );

    const hasFarmStake = computed(
      () => props.pool?.farm?.stake && props.pool?.farm?.stake > 0
    );

    const farmId = computed(() => props.pool?.farm?.id || '');
    const tokenAddress = computed(() => props.pool?.address);
    const hasFarmRewards = computed(() => props.pool?.farm?.rewards || 0 > 0);


    function trade() {
      emit('trade');
    }

    function onClose() {
      emit('close');
    }

    return {
      // methods
      fNum,
      onClose,
      approveLidoRelayer,
      approveToken,
      trade,
      farmId,
      tokenAddress,
      hasFarmRewards,
      // computed
      requiresApproval,
      fiatTotal,
      hasUnstakedBpt,
      hasFarm,
      requiresTokenApproval,
      hasFarmStake,
      requiresLidoRelayerApproval,
      isTokenApproved,
      isLidoRelayerApproved,
      valueIn,
      symbolIn,
      symbolOut,
      approvingLidoRelayer,
      approvingToken,
      lidoRelayerApproval,
      totalRequiredTransactions
    };
  }
});
